// Type-only, and it must stay type-only: store-config imports this module at
// runtime (sellerFor, printInfo derivation), so a runtime import back would
// complete a cycle and hit the temporal dead zone at module init.
import type { EntityId } from './store-config';

// The taxpayers behind the five stores. A store says which entity owns it
// (store-config.ts); this file says what that entity *is* — chiefly whether it
// is VAT registered, and from when.
//
// Transcribed from vat-regis-plan/VAT-REGISTRATION-PLAN.md §0.2 and §4.1.1 on
// 2026-08-05. That file is the planning document, this one is what ships.
//
// This registry is THE seller identity: tax-invoice printing, the output-tax
// report header, revenue attribution and the per-store printed tax
// registration all read from here (via sellerFor / toSellerPrintBlock /
// entityPrintInfo), and a VAT split is only ever computed for a taxpayer that
// actually owed VAT at the time.

export type { EntityId };

interface SellerEntityBase {
    id: EntityId;
    // Prints on tax invoices. Must match ภ.พ.20 character-for-character.
    legalName: string;
    // Prints on ORDINARY invoices — the brand the buyer recognises.
    tradingName: string;
    // Head-office/branch indicator as printed on documents. Individuals are
    // always the head office.
    branch: string;
    // 13 digits — company registration number, or national ID for an individual.
    // An identifier, never a number: leading zeros are significant.
    taxId: string;
    // Registered business address, copied exactly from ภ.พ.20.
    address: string;
    // '' when the entity has none — the print block wants strings, not gaps.
    website: string;
    email: string;
}

// A discriminated union rather than optional fields, so `vatEffectiveFrom`,
// the invoice series and the document archive folders are unreachable on a
// non-registered entity. The compiler then refuses the mistake that matters
// most here: issuing a tax invoice — or even preparing the plumbing for one —
// for `pensri`, whose whole position (plan §0.2) is staying under the ฿1.8M
// threshold and never charging VAT at all.
export type SellerEntity =
    | (SellerEntityBase & {
          vatRegistered: true;
          // ISO date. No sale before this date is a VAT sale, even for this
          // entity — registration is a property of the data, not a deploy.
          vatEffectiveFrom: string;
          // DOCUMENTARY — the number series actually minted by the output-tax
          // Lambda's NUMBERING map (lnwshop-insight-functions). Keep the two
          // in step; the Lambda is authoritative.
          invoicePrefix: string;
          // Per-registrant Google Drive folders. An RD audit of one taxpayer
          // means handing over one folder tree, so entities never share.
          reportDriveFolderId: string;
          // Issued tax-invoice PDFs (ต้นฉบับ archive — the copy of record).
          invoiceDriveFolderId: string;
          // Printed สำเนา copies from the manual path.
          invoiceCopyDriveFolderId: string;
          // Platforms whose tax invoices for this entity are issued in an
          // EXTERNAL system, not by the console's order flow. The bulk print
          // sends a listed platform's orders down the plain-invoice path,
          // surfacing a reminder popup only when an order carries confirmed
          // full-invoice buyer details; unlisted platforms keep the console
          // tax path. The manual form and month-end report over rows already
          // issued are unaffected.
          taxInvoiceExternalPlatforms?: readonly (
              'lazada' | 'shopee' | 'tiktok'
          )[];
      })
    | (SellerEntityBase & { vatRegistered: false });

// P1 (CONSOLE-BUILD-SPEC §1) is SETTLED: the `disorn` and `pensri` tax ids
// below were verified against the บัตรประชาชน copies on 2026-08-10. The
// verification overturned BOTH prior sources — disorn's id turned out to be
// the value the old per-store config had stamped everywhere, and pensri's is
// a third value neither file carried — which is why nothing else in the app
// holds a copy: this record is the single point of truth, and
// vat-regis-plan/store-entity-config-current.yaml mirrors it.
//
// `legalName` for the company is copied byte-for-byte from `sellerInfo` in
// pages/tax-invoice/models/constants.ts, which is what tax invoices print
// today. Note it uses จํากัด (U+0E4D + สระอา) where store-config.ts uses
// จำกัด (U+0E33) — visually identical, different bytes. The constants.ts form
// wins here because it is the one already on issued documents.
const ENTITIES: Record<EntityId, SellerEntity> = {
    'genlogic-co': {
        id: 'genlogic-co',
        legalName: 'บริษัท เจนเนอรัล ลอจิค จํากัด',
        // Confirmed 2026-08-10. Not consumed by any printed document yet;
        // ordinary invoices still print the per-store printInfo.name.
        tradingName: 'General Logic',
        branch: 'สำนักงานใหญ่',
        taxId: '0105567017277',
        address:
            '111/12 ซ.โชคชัยร่วมมิตร ถนนวิภาวดีรังสิต แขวงจอมพล เขตจตุจักร กรุงเทพมหานคร 10900',
        website: 'https://www.genlogic.co.th/',
        email: 'sale@genlogic.co.th',
        vatRegistered: true,
        vatEffectiveFrom: '2024-02-01',
        // Documentary — the Lambda's NUMBERING map mints the series:
        // daily INV{YYYY}{MM}{DD}{NNNN}, live since 2024. Never change
        // mid-stream; a changed live series reads as a gap.
        invoicePrefix: 'INV',
        // output-tax report uploads (was output-tax-summary.component.ts:251).
        reportDriveFolderId: '1DJ-x_9lpGB44dOIFeTjuN6csU85ubOmo',
        // Issued tax-invoice originals (was create-tax-invoice.component.ts).
        invoiceDriveFolderId: '1D7z7NjXZmyB8zH_EWb5E8D_rkij3aa4q',
        invoiceCopyDriveFolderId: '1Fkw7iMBx_itwsqRb1Z4f0H9nNnuGz9Ll',
        // Decided 2026-08-13 (refined same day to Lazada only): the
        // company's LAZADA tax invoices are issued in FlowAccount — those
        // orders print plain invoices, with a FlowAccount reminder popup
        // when confirmed full-invoice details exist. Shopee/TikTok orders
        // keep the console tax path (INV series).
        taxInvoiceExternalPlatforms: ['lazada'],
    },
    // Not VAT registered. The planned 1 October 2026 voluntary registration
    // was dropped on 2026-09-02; tax invoices for explicit customer requests
    // on disorn's stores are issued by the company where a store's
    // requestedTaxInvoiceEntityId says so (store-config.ts).
    disorn: {
        id: 'disorn',
        legalName: 'นาย ดิศรณ์ ตันติเกตุ',
        tradingName: 'GenLogic',
        branch: 'สำนักงานใหญ่', // individuals are always head office
        // Verified against the บัตรประชาชน copy 2026-08-10 (P1). This is the
        // id the old per-store config stamped on four stores — right for
        // disorn's, wrong for pensri's.
        taxId: '1101402050523',
        address:
            '111/12 ซ.โชคชัยร่วมมิตร ถนนวิภาวดีรังสิต แขวงจอมพล เขตจตุจักร กรุงเทพมหานคร 10900',
        website: 'https://www.genlogic.co.th/',
        email: 'sale@genlogic.co.th',
        vatRegistered: false,
        // Deliberately nothing else — see pensri below. (The output-tax
        // Lambda still carries an 'IND-' series for this entity; it is
        // unused while vatRegistered is false.)
    },
    // Deliberately not registering. If calendar-year revenue on
    // lazada-genlogic crosses ฿1.8M, registration becomes mandatory within 30
    // days (plan §4.16) — which is what the threshold monitor watches for.
    pensri: {
        id: 'pensri',
        legalName: 'เพ็ญศรี ตันติเกตุ',
        tradingName: 'General4 Group', // TODO confirm — pensri's own brand?
        branch: 'สำนักงานใหญ่', // individuals are always head office
        // Verified against the บัตรประชาชน copy 2026-08-10 (P1).
        taxId: '3100502607304',
        address:
            '111/12 ซ.โชคชัยร่วมมิตร ถนนวิภาวดีรังสิต แขวงจอมพล เขตจตุจักร กรุงเทพมหานคร 10900',
        website: 'https://www.genlogic.co.th/',
        email: 'sale@genlogic.co.th',
        vatRegistered: false,
        // Deliberately NOTHING else: no vatEffectiveFrom, no invoicePrefix,
        // no Drive folders. The union makes them unwritable while
        // vatRegistered is false — completing them would be the mistake.
    },
};

// Throws rather than returning undefined, matching storeById(): the argument is
// an EntityId, so a miss means the registry and the union have gone out of
// step, and a caller that carried on would attribute revenue to nobody.
export const entityById = (entityId: EntityId): SellerEntity => {
    const entity = ENTITIES[entityId];
    if (!entity) {
        throw new Error(`No entity configured for ${entityId}`);
    }
    return entity;
};

export const ALL_ENTITIES: SellerEntity[] = Object.values(ENTITIES);

export type VatRegisteredSellerEntity = Extract<
    SellerEntity,
    { vatRegistered: true }
>;

// For call sites that need the registered-only fields (invoice series, Drive
// folders) and are only ever reached for a registered entity — the manual
// tax-invoice path, the report uploads, the archive of an issued invoice.
// Throws rather than narrowing silently: reaching this with pensri means a
// guard upstream has already failed.
export const vatRegisteredEntityById = (
    entityId: EntityId,
): VatRegisteredSellerEntity => {
    const entity = entityById(entityId);
    if (!entity.vatRegistered) {
        throw new Error(`${entityId} is not VAT registered`);
    }
    return entity;
};

// The six fields a printed document carries for its seller, in the shape the
// jsreport templates expect (`sellerInfo.*`). Tax documents print the LEGAL
// name — the trading name is for ordinary invoices, which still come from the
// per-store printInfo.
export const toSellerPrintBlock = (
    entity: SellerEntity,
): {
    name: string;
    branch: string;
    taxId: string;
    website: string;
    address: string;
    email: string;
} => ({
    name: entity.legalName,
    branch: entity.branch,
    taxId: entity.taxId,
    website: entity.website,
    address: entity.address,
    email: entity.email,
});

// Whether a sale with this tax point owed VAT. Both arguments matter: an
// entity that is registered today was not registered for last year's sales,
// and regenerating an old period must not retroactively add VAT to them.
//
// `atIsoDate` is compared as a string, which is correct for ISO dates and
// avoids dragging a timezone into a question that is answered in Thai calendar
// days. Callers pass 'YYYY-MM-DD'.
export const isVatRegisteredAt = (
    entityId: EntityId,
    atIsoDate: string,
): boolean => {
    const entity = entityById(entityId);
    return entity.vatRegistered && atIsoDate >= entity.vatEffectiveFrom;
};

// The threshold at which VAT registration becomes mandatory, and the point at
// which we start warning. The warning is deliberately far below the threshold:
// crossing ฿1.8M starts a 30-day clock on a registration that takes weeks, so
// a warning at ฿1.8M arrives already too late to act on (plan §4.16).
export const VAT_THRESHOLD_THB = 1_800_000;
export const VAT_THRESHOLD_WARN_THB = 1_500_000;

// The VAT arithmetic lives in vat.ts: the rate is DATED there (vatRateAt), so
// a future change is one new entry and historical documents keep the rate
// they were issued under. Re-exported here so existing import sites keep
// working; new code should import from './vat' directly.
export { splitVatInclusive } from './vat';
